'use client';

import React from 'react';
import { composeClassName } from '@/utils/util_helpers';
import type { Contract } from '../../../../types';
import DiscountModal from '../discount-modal';
import DiscountInfoModal from '../discount-info-modal';
import EditDiscountModal from '../edit-discount-modal';
import ConfirmationDialog from '../confirmation-dialog/ConfirmationDialog';
import { useDiscounts } from '../../hooks/useDiscounts';
import DiscountHeader from './components/DiscountHeader';
import DiscountTable from './components/DiscountTable';
import { getDiscountCategoryLabel } from './utils';

export type ContractDiscountRowProps = {
  contract: Contract;
  index: number;
  onClose?: () => void;
};

const ContractDiscountRow: React.FC<ContractDiscountRowProps> = ({ contract, index, onClose }) => {
  const {
    discounts,
    showDiscountModal,
    showDiscountInfoModal,
    showEditDiscountModal,
    editingDiscount,
    selectedDiscount,
    handleAddDiscount,
    handleEditDiscount,
    handleDeleteDiscount,
    handleSubmitDiscount,
    handleSubmitEditDiscount,
    handleViewDiscount,
    setShowDiscountModal,
    setShowDiscountInfoModal,
    setShowEditDiscountModal,
    setEditingDiscount,
    setSelectedDiscount,
  } = useDiscounts(contract);

  const [showConfirmDeleteDialog, setShowConfirmDeleteDialog] = React.useState(false);
  const [discountToDeleteId, setDiscountToDeleteId] = React.useState<number | null>(null);

  const confirmDeleteDiscount = () => {
    if (discountToDeleteId !== null) {
      handleDeleteDiscount(discountToDeleteId);
      setShowConfirmDeleteDialog(false);
      setDiscountToDeleteId(null);
    }
  };

  const cancelDeleteDiscount = () => {
    setShowConfirmDeleteDialog(false);
    setDiscountToDeleteId(null);
  };

  const initiateDeleteDiscount = (discount: any) => {
    setDiscountToDeleteId(discount.id);
    setSelectedDiscount(discount);
    setShowConfirmDeleteDialog(true);
  };

  return (
    <div className={composeClassName('w-full')}>
      <div className="h-[1px] w-full bg-E50" />
      <div className="mx-7 my-3">
        <DiscountHeader
          hasDiscounts={discounts && discounts.length > 0}
          onAddDiscount={handleAddDiscount}
          onClose={onClose || (() => {})}
        />

        {discounts && discounts.length > 0 && (
          <DiscountTable
            discounts={discounts}
            onViewDiscount={handleViewDiscount}
            onDeleteDiscount={initiateDeleteDiscount}
            contractFullRent={contract.full_rent.cents / 100}
          />
        )}

        <DiscountModal
          open={showDiscountModal}
          onClose={() => {
            setShowDiscountModal(false);
            setEditingDiscount(null);
          }}
          onSubmit={handleSubmitDiscount}
          contract={contract}
        />
        <DiscountInfoModal
          open={showDiscountInfoModal}
          onClose={() => setShowDiscountInfoModal(false)}
          discount={selectedDiscount}
        />
        <EditDiscountModal
          open={showEditDiscountModal}
          onClose={() => {
            setShowEditDiscountModal(false);
            setEditingDiscount(null);
          }}
          onSubmit={handleSubmitEditDiscount}
          discount={editingDiscount}
        />
        <ConfirmationDialog
          isOpen={showConfirmDeleteDialog}
          onConfirm={confirmDeleteDiscount}
          onCancel={cancelDeleteDiscount}
          title="Remove discount"
          message={`Are you sure you want to remove this discount of €${
            selectedDiscount ? (selectedDiscount.amount_cents / 100).toFixed(2) : '0.00'
          } for ${
            selectedDiscount ? getDiscountCategoryLabel(selectedDiscount.discount_category) : ''
          }?`}
          confirmLabel="Remove"
          cancelLabel="Keep it"
        />
      </div>
    </div>
  );
};

export default ContractDiscountRow;
